












































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRefs,
  watch,
  vue3Set
} from '~/utils/nuxt3-migration'
import { Step } from '~/models/shared/types'
import CStep from '~/components/shared/configurable/stepper/CStep.vue'
import { useDep } from '~/compositions/dependency-container'
import ScrollService from '~/services/scroll/ScrollService'

export default defineComponent({
  components: { CStep },
  props: {
    withNumbers: { type: Boolean, default: false },
    selected: { type: Number, default: 0 },
    vertical: { type: Boolean, default: false },
    round: { type: Boolean, default: false },
    size: {
      type: String as PropType<'xs' | 'sm' | 'md' | 'lg'>,
      default: 'md'
    },
    steps: { type: Array as PropType<Step[]>, default: () => null },
    static: { type: Boolean, default: false },
    colorizeBar: { type: Boolean, default: false },
    showLabels: { type: Boolean, default: true }
  },
  setup(props, { emit }) {
    const { selected, vertical, round, size, steps } = toRefs(props)

    const scrollService = useDep(ScrollService)

    const scrollableCategories = ref<HTMLElement | null>(null)
    const stepsData = ref(steps.value.slice())
    const stepsRefs = ref(null)
    const hasSelected = ref(steps.value.some(s => s.selected))
    const currentStep = ref(selected.value || 0)

    watch(steps, () => {
      if (stepsData.value) {
        stepsData.value = normalizeSteps(steps.value, true)
      }
    })

    watch(selected, newSelectedStep => {
      setSelectedStep(newSelectedStep)
    })

    const selectedIndex = computed(
      () =>
        (props.colorizeBar || props.static) &&
        stepsData.value.findIndex(step => step.selected)
    )
    const numberOfSteps = computed(() => stepsData.value?.length)
    const verticalSizeClasses = computed(() => {
      if (vertical.value && size.value === 'xs') {
        return ['size-xs']
      }
    })

    const roundSizeClasses = computed(() => {
      if (size.value === 'lg') {
        return 'tw-h-36 tw-w-36 tw-text-lg'
      } else if (size.value === 'sm') {
        return 'tw-h-24 tw-w-24 tw-text-base'
      } else if (size.value === 'xs') {
        return 'tw-h-16 tw-w-16 tw-text-base'
      }
      return 'tw-h-32 tw-w-32 tw-text-lg'
    })

    const strokeDashArray = computed(() => {
      const stepSize = 220 / numberOfSteps.value
      const step = currentStep.value + 1
      return step * stepSize + 220
    })

    const normalizeSteps = (
      steps: Array<Step>,
      setSelected: boolean = false
    ) => {
      return (steps || []).slice().map((step, i) => ({
        ...step,
        selected: setSelected ? selected.value === i : step.selected,
        vertical: vertical.value,
        round: round.value
      }))
    }

    if (stepsData.value) {
      stepsData.value = normalizeSteps(steps.value, true)
    }

    const nextStep = () => {
      setStepDataSelectedStatus(currentStep.value, false)
      currentStep.value = getNextStep()
      setStepDataSelectedStatus(currentStep.value, true)
      emit('change', currentStep.value)
      if (scrollableCategories.value) {
        scrollableCategories.value.scrollLeft =
          scrollableCategories.value.children[currentStep.value].offsetLeft - 50
      }
    }

    const prevStep = () => {
      setStepDataSelectedStatus(currentStep.value, false)
      currentStep.value = getPrevStep()
      setStepDataSelectedStatus(currentStep.value, true)
      emit('change', currentStep.value)
      if (scrollableCategories.value) {
        scrollableCategories.value.scrollLeft =
          scrollableCategories.value.children[currentStep.value].offsetLeft - 50
      }
    }

    const getNextStep = () => {
      let index = currentStep.value
      stepsData.value.slice(currentStep.value + 1).some((step, i) => {
        if (!step.disabled) {
          index = currentStep.value + (i + 1)
          return true
        }
        return false
      })
      return index
    }

    const getPrevStep = () => {
      let index = currentStep.value
      stepsData.value.slice(0, currentStep.value).forEach((step, i) => {
        if (!step.disabled) {
          index = i
        }
      })
      return index
    }

    const setSelectedStep = (stepNumber: number | null = null) => {
      if (stepNumber === currentStep.value || !Number.isFinite(stepNumber)) {
        return
      }

      setStepDataSelectedStatus(currentStep.value, false)
      currentStep.value = stepNumber
      setStepDataSelectedStatus(currentStep.value, true)
      emit('change', stepNumber)

      if (scrollableCategories.value?.children) {
        scrollService.scrollIfOverflown(
          scrollableCategories.value?.children[currentStep.value],
          scrollableCategories.value,
          50
        )
      }
    }
    const setStepDataSelectedStatus = (index: number, selected: boolean) => {
      vue3Set(stepsData.value, index, {
        ...stepsData.value[index],
        selected
      })
    }

    function handleStepClick(step: Step, index: number) {
      emit('click-step', index)

      if (props.static || step.disabled) {
        return
      }

      setSelectedStep(index)
    }

    function isPreviousCompleted(index: number) {
      if (!steps.value?.length) {
        return false
      }
      if (index > 0) {
        return steps.value[index - 1].completed
      }

      return steps.value[index].completed
    }

    return {
      handleStepClick,
      setSelectedStep,
      nextStep,
      prevStep,
      currentStep,
      strokeDashArray,
      numberOfSteps,
      roundSizeClasses,
      stepsRefs,
      hasSelected,
      stepsData,
      verticalSizeClasses,
      scrollableCategories,
      selectedIndex,
      isPreviousCompleted
    }
  }
})
